import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Phone, Video } from "lucide-react";
import axios from "axios";

const StartCallButton = ({ clientId, doctorId, patient }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [loading, setLoading] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [callInfo, setCallInfo] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  const hasActiveCall = useMemo(() => {
    const expiresAt = patient?.meeting?.expiresAt
      ? new Date(patient.meeting.expiresAt).getTime()
      : 0;

    return Boolean(
      patient?.meeting?.active &&
        patient?.meeting?.doctorLink &&
        expiresAt > now
    );
  }, [now, patient?.meeting]);

  const callActionText = hasActiveCall ? "Go to call" : "Start call now";

  // 🔹 open menu
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // 🔹 close menu
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleGoToCall = async () => {
    try {
      setLoading(true);
  
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/meeting/create`,
        {
          clientId,
          doctorId,
        }
      );
  
      const { doctorLink, expiresAt, patientPaymentRequired, alreadyActive } = res.data;
  
      if (!doctorLink) {
        throw new Error("Meeting link was not returned from server");
      }
  
      setCallInfo({
        doctorLink,
        expiresAt,
        patientPaymentRequired: Boolean(patientPaymentRequired),
        alreadyActive: Boolean(alreadyActive),
      });
      setCallModalOpen(true);
    } catch (err) {
      console.error("START CALL ERROR:", err);
  
      let message = "Failed to start call";
  
      // 🔥 Backend responded with error
      if (err.response) {
        const data = err.response.data;
  
        message =
          data?.error ||
          data?.message ||
          `Server error (${err.response.status})`;
  
      // 🔥 Request made but no response (network issue)
      } else if (err.request) {
        message = "Network error — check your connection";
  
      // 🔥 Something else (code issue)
      } else {
        message = err.message || "Unexpected error occurred";
      }
  
      alert(message);
    } finally {
      setLoading(false);
      handleClose();
    }
  
  };

  const closeCallModal = () => {
    setCallModalOpen(false);
  };

  const openDoctorCall = () => {
    if (!callInfo?.doctorLink) return;
    window.location.href = callInfo.doctorLink;
  };

  return (
    <>
      {/* 🔹 ICON BUTTON */}
      <IconButton
        onClick={handleClick}
        size="small"
        className="bg-[#F0F4FF] hover:bg-[#E4EAFF]"
      >
        <Phone size={18} className="text-[#1E0E62]" />
      </IconButton>

      {/* 🔹 MENU */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          className: "rounded-xl shadow-md mt-2",
        }}
      >
        <MenuItem onClick={handleGoToCall} disabled={loading}>
          <ListItemIcon>
            <Video size={18} />
          </ListItemIcon>
          <ListItemText>
            {loading ? "Preparing..." : callActionText}
          </ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={callModalOpen}
        onClose={closeCallModal}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Call initiated</DialogTitle>
        <DialogContent>
          <div className="space-y-3 text-sm text-slate-600">
            <p>
              The call room is ready for {patient?.name || "this patient"}.
            </p>
            {callInfo?.patientPaymentRequired ? (
              <p>
                This patient is on the free tier, so we sent them a call access
                payment prompt. You can go to the call room and wait while they
                complete payment.
              </p>
            ) : (
              <p>The patient has access, so their join link has been sent.</p>
            )}
            {callInfo?.alreadyActive && (
              <p>An active call already exists, so this will reopen it.</p>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCallModal} color="inherit">
            Close
          </Button>
          <Button onClick={openDoctorCall} variant="contained">
            Go to call
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StartCallButton;
